import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { ToggleLeftIcon } from '@phosphor-icons/react/dist/csr/ToggleLeft';
import { ToggleRightIcon } from '@phosphor-icons/react/dist/csr/ToggleRight';
import { TrashIcon } from '@phosphor-icons/react/dist/csr/Trash';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import type { ReferenceNode } from '../../lib/api/reference';
import styles from './ReferenceLifecycleActions.module.css';

/**
 * The row actions shared by all three levels of the reference hierarchy —
 * Edit, Activate/Deactivate and Delete — parameterised over the level's own
 * `activate`/`deactivate`/`remove` calls rather than duplicated three times.
 * Follows `UserActions.tsx` for the shape: activate does not confirm (it
 * takes nothing away) and gets its own row-scoped failure notice; deactivate
 * and delete each confirm inside their own still-open `Dialog`.
 *
 * ## The 409 IS THE MESSAGE
 *
 * `ReferenceLifecycleGuard::assertNoLiveChildren()` (API repository) answers
 * a 409 whose `error.details.children[0]` is already a full, SERVER-LOCALISED
 * sentence — `__('errors.reference_dispensary_has_live_cities', ['count' =>
 * ...])`, resolved against `Accept-Language` (`client.ts`'s own
 * `acceptLanguage()`), which is the same header this client sets on every
 * request. There is no fixed vocabulary here for this component to translate
 * itself into two languages (contrast `UserActions.tsx`'s `describe()`, which
 * branches on `error.code` into COPY THIS CLIENT OWNS) — the server has
 * already said, in the caller's own language, exactly what is blocking the
 * action and how many. Rendering anything else would be a worse message than
 * the one the response already carries.
 *
 * ## `onDeleted` versus `onChanged`
 *
 * Activate and deactivate return the updated resource, merged into the row
 * the same way `UserActions.onChanged` merges an account — no refetch, so a
 * change to one row does not flash a loading skeleton over the rest of the
 * table. Delete returns nothing to merge: the row is gone, and `onDeleted`
 * is what tells the parent to remove it from `rows` rather than update it.
 */

export interface ReferenceLifecycleActionsProps<T extends ReferenceNode> {
  node: T;
  /** The name shown in confirmation dialogs — already resolved to the
   *  caller's UI language by whoever renders this row. */
  label: string;
  /** `permissions.includes('reference.manage')` — computed once by the
   *  caller rather than re-derived here. */
  canManage: boolean;
  onEdit: (node: T) => void;
  onChanged: (uuid: string, changes: Partial<T>) => void;
  onDeleted: (uuid: string) => void;
  activate: (uuid: string) => Promise<T>;
  deactivate: (uuid: string) => Promise<T>;
  remove: (uuid: string) => Promise<void>;
}

const CONFLICT_CODE = 'conflict';
const FORBIDDEN_CODE = 'forbidden';

type Pending = 'deactivate' | 'delete';

export function ReferenceLifecycleActions<T extends ReferenceNode>({
  node,
  label,
  canManage,
  onEdit,
  onChanged,
  onDeleted,
  activate,
  deactivate,
  remove,
}: ReferenceLifecycleActionsProps<T>) {
  const { t } = useTranslation();

  const [pending, setPending] = useState<Pending | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  const [activateBusy, setActivateBusy] = useState(false);
  const [activateFailure, setActivateFailure] = useState<string | null>(null);
  const [activateReference, setActivateReference] = useState<string | null>(null);

  /** See `UserActions.tsx`'s identical field: a ref because two clicks in one
   *  React batch both read `submitting` as it was before either. */
  const inFlight = useRef(false);

  function describe(error: ApiError): string {
    switch (error.code) {
      case CONFLICT_CODE:
        // See the component docblock: `details.children[0]` is already a
        // full, server-localised sentence naming the live count. Only a
        // conflict shaped some OTHER way (no `children` detail at all) falls
        // through to the generic copy below.
        return error.details['children']?.[0] ?? t('reference.actionFailed');
      case FORBIDDEN_CODE:
        return t('reference.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('reference.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('reference.actionFailed');
    }
  }

  function renderFailure(failureText: string | null, referenceText: string | null): ReactNode {
    return (
      <div className={styles['failure']} role="alert" aria-atomic="true">
        {failureText === null ? null : <span className={styles['failureText']}>{failureText}</span>}
        {referenceText === null ? null : (
          <span className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid="referenceActionsRequestId">
              {referenceText}
            </bdi>
          </span>
        )}
      </div>
    );
  }

  function openDialog(action: Pending): void {
    setPending(action);
    setFailure(null);
    setReference(null);
  }

  function closeDialog(): void {
    setPending(null);
    setFailure(null);
    setReference(null);
  }

  async function handleActivate(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setActivateBusy(true);
    setActivateFailure(null);
    setActivateReference(null);

    try {
      const updated = await activate(node.uuid);
      onChanged(node.uuid, updated);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setActivateFailure(describe(error));
      setActivateReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setActivateBusy(false);
    }
  }

  async function confirmDeactivate(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setReference(null);

    try {
      const updated = await deactivate(node.uuid);
      onChanged(node.uuid, updated);
      closeDialog();
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      // NOT closeDialog(): kept open so the 409's own sentence is readable.
      setFailure(describe(error));
      setReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setReference(null);

    try {
      await remove(node.uuid);
      onDeleted(node.uuid);
      closeDialog();
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setFailure(describe(error));
      setReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const canActivate = canManage && !node.is_active;
  const canDeactivate = canManage && node.is_active;

  return (
    <div className={styles['actions']}>
      {canManage ? (
        <Button
          variant="ghost"
          onClick={() => {
            onEdit(node);
          }}
          icon={<PencilSimpleIcon size={16} />}
        >
          {t('reference.edit')}
        </Button>
      ) : null}

      {canActivate ? (
        <Button
          variant="ghost"
          loading={activateBusy}
          onClick={() => {
            void handleActivate();
          }}
          icon={<ToggleRightIcon size={16} />}
        >
          {t('reference.activate')}
        </Button>
      ) : null}

      {canDeactivate ? (
        <Button variant="ghost" onClick={() => openDialog('deactivate')} icon={<ToggleLeftIcon size={16} />}>
          {t('reference.deactivate')}
        </Button>
      ) : null}

      {canManage ? (
        <Button variant="ghost" onClick={() => openDialog('delete')} icon={<TrashIcon size={16} />}>
          {t('reference.delete')}
        </Button>
      ) : null}

      {/* Activate's own failure — mounted whenever it can be offered at all,
          independent of `pending`, exactly as UserActions.tsx's does. */}
      {canActivate ? renderFailure(activateFailure, activateReference) : null}

      <Dialog
        open={pending === 'deactivate'}
        destructive
        title={t('reference.confirmDeactivateTitle', { name: label })}
        onClose={closeDialog}
        actions={
          <>
            <Button
              variant="destructive"
              loading={submitting}
              onClick={() => {
                void confirmDeactivate();
              }}
              icon={<ToggleLeftIcon size={16} />}
            >
              {t('reference.confirm')}
            </Button>
            <Button variant="ghost" onClick={closeDialog} icon={<XIcon size={16} />}>
              {t('reference.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['body']}>{t('reference.confirmDeactivateBody', { name: label })}</p>
        {renderFailure(failure, reference)}
      </Dialog>

      <Dialog
        open={pending === 'delete'}
        destructive
        title={t('reference.confirmDeleteTitle', { name: label })}
        onClose={closeDialog}
        actions={
          <>
            <Button
              variant="destructive"
              loading={submitting}
              onClick={() => {
                void confirmDelete();
              }}
              icon={<TrashIcon size={16} />}
            >
              {t('reference.confirm')}
            </Button>
            <Button variant="ghost" onClick={closeDialog} icon={<XIcon size={16} />}>
              {t('reference.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['body']}>{t('reference.confirmDeleteBody', { name: label })}</p>
        {renderFailure(failure, reference)}
      </Dialog>
    </div>
  );
}
